import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn.js';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-700',
  // Aksent rang faqat MAQSADLI harakatlar uchun (dizayn tizimi §1)
  secondary: 'bg-accent-600 text-white hover:bg-accent-700 active:bg-accent-700',
  outline: 'border border-n-200 bg-white text-n-800 hover:bg-n-50',
  ghost: 'text-n-800 hover:bg-n-100',
  danger: 'bg-error-600 text-white hover:bg-error-600',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Yuklanish holati — tugma bloklanadi va aylanma ko'rsatiladi. */
  loading?: boolean;
  icon?: ReactNode;
};

/**
 * Tugma.
 *
 * `loading` holatida tugma O'CHIRILADI: aks holda foydalanuvchi ikki marta
 * bosib, ikkita ariza yoki ikkita mahsulot yaratib yuborardi.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});
